import React, { useState } from "react";
import { APPLY_YEAR_ON_SELECT_VIEW2 } from "../Util/constants";
import { topicDictionary } from "../Util/tileDictionary";
import LocationDropdown from "./LocationDropdown";
import YearDropdown from "./YearDropdown";
import DataVisualizationToggle from "./DataVisualizationToggle";

import ShareLink from "./ShareLink";
import SubQuestions from "./SubQuestions";
import * as analytics from "../Util/Analytics/QuestionAnalytics";
import ComparisonSelector from "./ComparisonSelector";
import SampleSizeToggle from "./SampleSizeToggle";

const QuestionHeader = ({
  displayLocationDropdown,
  displayLocations,
  handleSubQuestionChange,
  isPatternsOverTime,
  locations,
  onVizTypeChange,
  onLocationChange,
  onYearChange,
  onCompareSecondLocation,
  onReturnToLocationList,
  onReturnToLocationSubQuestionList,
  selectedData,
  showSampleSize,
  setShowSampleSize,
  setLocationSelectedFromDropdownMenu,
  setIsPatternsOverTime,
  showReturnToLocationView,
  showReturnToSubQuestionView,
  shortNavFootNoteSymbol,
  subQuestion,
  setDataLoading,
  subQuestionLoading,
  infoBannerMessage,
}) => {
  const question = selectedData ? selectedData.question : null;
  const subQuestionValue = selectedData ? selectedData.subQuestionValue : null;
  const location = selectedData ? selectedData.location : null;
  const vizType = selectedData ? selectedData.vizType : null;
  const locationAbbr = selectedData ? selectedData.location.LocationAbbr : null;
  const locationDesc = selectedData ? selectedData.location.LocationDesc : null;
  const icon =
    question && question.TopicId ? topicDictionary[question.TopicId].icon : "";

  function handleSelectAnotherQuestion() {
    const hasTopicFilterInUrl = (window?.location?.hash || "").includes("topicId=");
    analytics.selectBreadcrumbDestination({ destination: hasTopicFilterInUrl ? "HOME-TOPIC" : "HOME" });
    onVizTypeChange({ questionId: null });
  }

  function handleAllLocationsBreadcrumbClick() {
    analytics.selectBreadcrumbDestination({ destination: "ALL L-1Q" });
    onReturnToLocationList && onReturnToLocationList();
  }

  function handleLocationResultsBreadcrumbClick() {
    analytics.selectBreadcrumbDestination({ destination: "1L-ALLQ" });
    setIsPatternsOverTime(false);
    setLocationSelectedFromDropdownMenu(false);
    onReturnToLocationSubQuestionList && onReturnToLocationSubQuestionList();
  }

  function handleSubQuestionOverTimeBreadcrumbClick() {
    analytics.selectBreadcrumbDestination({ destination: "1L-1Q" });
  }

  const questionId =
    question && question.QuestionId ? question.QuestionId : null;
  const subQuestionId =
    subQuestion && subQuestion.SubQuestionId ? subQuestion.SubQuestionId : null;
  const isAllLocations = locationAbbr === "ALL";
  // Treat a subquestion as "selected" only when we have a
  // corresponding subQuestion object, not just a URL param.
  // This ensures that stale/invalid subQuestionId values in the URL
  // do not affect comparison logic.
  const hasSubQuestionSelected = !!subQuestion;
  const [showComparison, setShowComparison] = useState(false);
  // Pending year selection used in View #2 (specific location, no subquestion, compare link visible)
  const [pendingYear, setPendingYear] = useState(selectedData?.year);
  const isDeferredView2YearMode = !isPatternsOverTime && !hasSubQuestionSelected && !APPLY_YEAR_ON_SELECT_VIEW2;
  const effectiveCurrentYear = isDeferredView2YearMode
    ? (pendingYear ?? selectedData?.year)
    : selectedData?.year;

  // View detection flags
  // - View 1: All Locations + Question + Subquestion
  // - View 2: Specific Location (Nationwide/State/Local) + Question (subquestion optional)
  // - View 3: Nationwide + Question + Subquestion
  const isNationwide =
    !!location && (
      location.LocationType === "National" ||
      location.LocationAbbr === "Nationwide" ||
      location.LocationDesc === "Nationwide" ||
      location.LocationAbbr === "US" ||
      location.LocationAbbr === "XX"
    );
  const headerVariant =
    isAllLocations && hasSubQuestionSelected
      ? "qh-variant--all-locations-subquestion" // View 1
      : isNationwide && hasSubQuestionSelected
      ? "qh-variant--nationwide-subquestion" // View 3
      : "qh-variant--specific-location"; // View 2 (default for non-ALL)

  // Derive a simple view class for styling hooks across components
  // qh-view-1: All Locations + subquestion (View #1)
  // qh-view-2: Specific location, no subquestion (View #2)
  // qh-view-3: Specific location + subquestion OR POT (View #3)
  const currentViewClass = isAllLocations
    ? "qh-view-1"
    : (hasSubQuestionSelected || isPatternsOverTime)
    ? "qh-view-3"
    : "qh-view-2";

  /**
   * Comparison views (header-level)
   * - View 1: ALL + 1 Question → year-only compare (ComparisonSelector mode='allLocations').
   * - View 2: One Location + All Questions → location+year compare (questionAndLocation, showYear=true), toggled via link.
   * - View 3: One Location + 1 Question → location-only compare (questionAndLocation, showYear=false), always visible.
   *
   * Table scenarios (rendered in Table.js for reference):
   * 1) No subquestions, ALL locations → ALL states/locals tables.
   * 2) With subquestions, ALL locations → ALL states grouped tables.
   * 3) Specific location, subquestion list → location’s subquestions.
   * 4) Patterns Over Time → POT table when available.
   * 5) No data message → patterns and non-patterns fallbacks.
   */

  function deriveComparisonUI() {
    // View 1 (ALL locations): header-level comparison appears ONLY when
    // the question has no subquestions (Scenario 1) and the summary text
    // is being shown (and not POT). When subquestions exist (Scenario 2),
    // comparison lives with the tables, not in the header.
    if (isAllLocations) {
      const hasAnySubQs = !!(question?.SubQuestions && question.SubQuestions.length > 0);
      const noSubQs = !hasAnySubQs;
      const showingSummary = !!(
        question?.SummaryText && String(question.SummaryText).trim().length > 0
      );

      // If this is the "All Locations + subquestion" view (View #1 in
      // the location-text spec), do NOT render the header comparison UI.
      if (hasAnySubQs) {
        return { showSelector: false };
      }

      const shouldShow = noSubQs && showingSummary && !isPatternsOverTime;
      return shouldShow
        ? {
            showSelector: true,
            title: "Comparison Year",
            mode: "allLocations",
            showYear: true,
            alwaysVisible: true,
          }
        : { showSelector: false };
    }
    // When Patterns Over Time is active for a specific location, still allow
    // location comparison (location-only to keep UI simple in POT context).
    if (isPatternsOverTime) {
      return {
        showSelector: true,
        title: "Comparison Location",
        mode: "questionAndLocation",
        showYear: false,
        alwaysVisible: true,
      };
    }
    // View 3: specific location + subquestion selected → always show, location-only
    if (hasSubQuestionSelected) {
      return {
        showSelector: true,
        title: "Comparison Location",
        mode: "questionAndLocation",
        showYear: false,
        alwaysVisible: true,
      };
    }
    // View 2: specific location, no subquestion → gated behind toggle link
    return {
      showSelector: showComparison,
      title: "Comparison Location/Year",
      mode: "questionAndLocation",
      showYear: true,
      alwaysVisible: false,
    };
  }

  // Keep pendingYear in sync when the actual selected year changes from elsewhere
  // or when navigating to a new question/location
  React.useEffect(() => {
    setPendingYear(selectedData?.year);
  }, [selectedData?.year, questionId, locationAbbr]);

  // Determine when either a subtopic (subquestion) or the
  // All-Locations summary text view is effectively active.
  // This is used to decide when the "Show sample size" toggle
  // should be visible at all.
  const hasAllLocationsSummaryDetail = (
    isAllLocations &&
    (!question?.SubQuestions || question.SubQuestions.length === 0) &&
    !!(question?.SummaryText && String(question.SummaryText).trim().length > 0)
  );

  // Treat View #3 (Patterns Over Time / specific location with detail)
  // as having a "detail" selection so the Show Sample Size checkbox
  // is available there as well.
  // For sample-size visibility, treat only a real subquestion selection
  // as a "detail" view. This keeps the Show Sample Size checkbox
  // hidden when no valid subquestion is selected.
  const hasDetailSelection = hasSubQuestionSelected;

  return (
    <div>
      <div className="row question-header">
        <div
          aria-label="Question"
          className={
            "question-header__topic-subtopic-header mb-4 yrbs-cat__color--" +
            question.TopicId
          }
          style={{
            backgroundColor: topicDictionary[question.TopicId].color,
          }}
        >
          <div tabIndex="0" role="link">
            <div className="row tile-header">
              <div className="col-2" aria-hidden="true"></div>
              <div className="col-12 p-2">
                <span className={"topic-icon pr-2 " + icon}></span>
                <span className="topic-description">
                  {question.TopicDescription}
                </span>
                -
                <span className="subtopic-description">
                  {question.SubTopicDescription}
                </span>
              </div>
            </div>

            <h1 className="question-description p-2">
              {question.LongQuestionDescription}&nbsp;{shortNavFootNoteSymbol}
              <br />
              <span
                style={{
                  fontStyle: "italic",
                  fontSize: "1rem",
                  fontWeight: "normal",
                }}
              >
                {question.SurveyText}
              </span>
            </h1>
          </div>
          <div className="d-flex question-header__location-section justify-content-center">
            <div className="col-sm-12 col-md-12 col-lg-5 p-2 d-flex justify-content-center">
              <LocationDropdown
                onLocationChange={onLocationChange}
                locationAbbr={locationAbbr}
                location={location}
                setLocationSelectedFromDropdownMenu={
                  setLocationSelectedFromDropdownMenu
                }
                displayLocations={displayLocations}
                displayLocationDropdown={displayLocationDropdown}
                locations={locations}
              ></LocationDropdown>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div id="breadcrumbs" className="col-10 question-header__question-controls p-0">
          {(showReturnToLocationView || showReturnToSubQuestionView) && (
            <div>
              <button className="btn btn-link btn-sm" style={{ fontSize: "20px" }} onClick={handleSelectAnotherQuestion}>Home</button>
              <span className="btn btn-sm text-decoration-none" style={{ fontSize: "20px", cursor: "text" }}> &gt; </span>
              {locationAbbr === "ALL" ? (
                <span className="btn btn-sm text-decoration-none" style={{ fontSize: "20px", cursor: "text", paddingLeft: "5px" }}>
                  All Locations Survey Results
                </span>
              ) : (
                <>
                  <button
                    className="btn btn-link btn-sm"
                    style={{ fontSize: "20px" }}
                    onClick={handleAllLocationsBreadcrumbClick}
                  >
                    All Locations Survey Results
                  </button>
                  <span className="btn btn-sm text-decoration-none" style={{ fontSize: "20px", cursor: "text" }}> &gt; </span>
                  {showReturnToSubQuestionView ? (
                    <button
                      className="btn btn-link btn-sm"
                      style={{ fontSize: "20px" }}
                      onClick={handleLocationResultsBreadcrumbClick}
                    >
                      {location.LocationType === "National" ? "Nationwide Results" : `${location.LocationDesc} Survey Results`}
                    </button>
                  ) : (
                    <>
                      {location.LocationType === "National" ? (
                        <span className="btn btn-sm text-decoration-none" style={{ fontSize: "20px", cursor: "text", paddingLeft: "5px" }}>Nationwide Survey Results</span>
                      ) : (
                        <span className="btn btn-sm text-decoration-none" style={{ fontSize: "20px", cursor: "text", paddingLeft: "5px" }}>{ location.LocationDesc } Survey Results</span>
                      )}
                    </>
                  )}
                  {/* Add breadcrumb segment for Subquestion Over Time view */}
                  {isPatternsOverTime && hasSubQuestionSelected && (
                    <>
                      <span className="btn btn-sm text-decoration-none" style={{ fontSize: "20px", cursor: "text" }}> &gt; </span>
                      <span
                        className="btn btn-sm text-decoration-none"
                        style={{ fontSize: "20px", cursor: "text", paddingLeft: "5px" }}
                        onClick={handleSubQuestionOverTimeBreadcrumbClick}
                      >
                        Subquestion Over Time
                      </span>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        <div className="col-xs-12 col-sm-12 col-md-12 col-lg-2 p-0 text-right">
          <ShareLink />
        </div>
      </div>
      <hr className="mt-2 mb-2"/>
      {/* When a subQuestionId is present in the URL but does not
          resolve to a known subQuestion object, surface a small
          notice so the user understands why no subtopic is shown. */}
      {subQuestionValue && !subQuestion && (
        <div className="row mt-4 mb-2">
          <div className="col-12">
            <div className="alert alert-info p-2" role="status">
              The selected subtopic is not available for this question and year.
            </div>
          </div>
        </div>
      )}
      {/* Info banner for user notifications (e.g., location reset for map view) */}
      {infoBannerMessage && (
        <div className="row mt-2 mb-2">
          <div className="col-12">
            <div className="alert alert-info p-2" role="status" style={{ fontSize: "0.9em" }}>
              {infoBannerMessage}
            </div>
          </div>
        </div>
      )}
      {/*
        Header view variants and what renders in each:
        - View 1 (qh-variant--all-locations-subquestion):
            * Subtopic heading used for .question-header__location-text
            * VisualizationToggle appears below the subtopic (rendered elsewhere for ALL context)
            * Comparison shows "Comparison Year" only
        - View 2 (qh-variant--specific-location):
            * Location text used for .question-header__location-text ("{LocationDesc} Survey Results" rules)
            * DataVisualizationToggle at top of header (Table/Graph only)
            * Year dropdown + optional comparison (location + year)
        - View 3 (qh-variant--nationwide-subquestion):
            * Subtopic heading used for .question-header__location-text
            * Do NOT render DataVisualizationToggle in this view (per spec)
            * Comparison location controls appear (location-only compare UI section)
      */}
      <div className={`row question-header__variant ${headerVariant}`}>
          {isPatternsOverTime ? (
              ""
            ) : (
              <h2 className="col-12 col-lg-4 question-header__location-text">
                {selectedData.location.LocationDesc === "Nationwide"
                  ? `${locationDesc} Results`
                  : `${locationDesc} Survey Results`}
              </h2>
            )}
            {(() => {
              // Adjust DataVisualizationToggle presentation per view and grid widths:
              // - When location text is shown on the left (col-lg-5), use col-lg-7 for the toggle column so the row sums to 12.
              // - In other cases (e.g., POT or alternate variants), fall back to full width.
              // - View #3 (nationwide + subquestion): hide the toggle entirely (handled by showVizToggle below).
              // - For ALL locations: only show toggle for graph view (table and map have their own toggles)
              const vizColClass = !isPatternsOverTime
                ? "col-12 col-lg-4 p-0 "
                : "col-12 p-0";
              // Show viz toggle for:
              // Specific locations (not ALL) except nationwide+subquestion view
              // ALL locations: each view (table, map, graph) has its own toggle inside the component
              // Do NOT show for ALL locations when no subquestion is selected yet
              const showVizToggle =
                locationAbbr !== "ALL" && headerVariant !== "qh-variant--nationwide-subquestion";
              return (
                <div className={`${vizColClass} question-header__viz-toggle ${headerVariant}`}>
                  {showVizToggle && (
                    <DataVisualizationToggle
                      onVizTypeChange={onVizTypeChange}
                      vizType={vizType}
                      question={question}
                      subQuestionValue={subQuestionValue}
                      locationAbbr={locationAbbr}
                      allowedOptions={
                        headerVariant === "qh-variant--specific-location"
                          ? ["table", "graph"]
                          : ["table", "graph", "map"]
                      }
                    />
                  )}
                </div>
              );
            })()}
            <div className="col-12 col-lg-4">
            </div>
          </div>



      {/* Year and comparison controls (also show selector in POT for specific locations) */}
      {/* Hide year/comparison controls for Graph view when a specific location is selected */}
      {!(vizType === "graph" && !isAllLocations) && (
        <div
          className={`mb-2 ${(!isAllLocations && deriveComparisonUI().showYear === false) ? "" : "d-flex"} align-items-start question-header__year-comparison-controls question-header__year-comparison-controls--header ${currentViewClass}`}
        >
        <div className="current-location-year  align-top" >
          {isAllLocations ? (
            <div className="year-inline-wrapper d-flex align-items-center">
              <label className="year-inline-wrapper__label" htmlFor="current-year-dropdown">Year</label>
              <YearDropdown
                onYearChange={onYearChange}
                year={selectedData.year}
                questionId={questionId}
                subQuestionId={subQuestionId}
                locationAbbr={locationAbbr}
                dropdownID="current-year-dropdown"
                setDataLoading={setDataLoading}
                subQuestionText={subQuestion ? subQuestion.SubQuestion : ""}
                summaryText={(!question?.SubQuestions || question.SubQuestions.length === 0) ? (question ? question.SummaryText : "") : ""}
              />
            </div>
          ) : isPatternsOverTime || hasSubQuestionSelected ? (
            <div className="d-flex flex-column">
              {subQuestion && subQuestion.SubQuestion ? (
                <h2 className="mt-2 mb-2 main-table-header question-header__location-text" style={{ fontSize: '30px' }}>
                  {subQuestion.SubQuestion}
                </h2>
              ) : (
                question && question.ShortQuestionDescription ? (
                  <h2 className="mt-2 mb-2 main-table-header question-header__location-text" style={{ fontSize: '30px' }}>
                    {question.ShortQuestionDescription}
                  </h2>
                ) : null
              )}
              <div className="mb-3 location-text-view-3" >Location: {locationDesc}</div>
            </div>
          ) : (
            <>
              {/** When comparison panel is open, add a heading above the current controls */}
              {showComparison && (
                <div className="my-2" style={{ fontWeight: 800, fontSize: 16 }}>
                  Current Location/Year
                </div>
              )}
              <div className="d-flex align-items-center">
                <span className="current-location-year__label">{showComparison ? (locationDesc || locationAbbr) : "Year"}</span>
                <div className="current-location-year__value-row d-flex align-items-center">
                  {/* In View 2 (no POT, no subquestion, and compare panel closed),
                      the dropdown should NOT auto-apply; capture into pendingYear instead. */}
                  {!isPatternsOverTime && !hasSubQuestionSelected && !showComparison && !APPLY_YEAR_ON_SELECT_VIEW2 ? (
                    <YearDropdown
                      onYearChange={(obj) => setPendingYear(obj?.Year)}
                      year={effectiveCurrentYear}
                      questionId={questionId}
                      subQuestionId={subQuestionId}
                      locationAbbr={locationAbbr}
                      dropdownID="current-year-dropdown"
                      setDataLoading={setDataLoading}
                      subQuestionText={subQuestion ? subQuestion.SubQuestion : ""}
                      summaryText={(!question?.SubQuestions || question.SubQuestions.length === 0) ? (question ? question.SummaryText : "") : ""}
                    />
                  ) : (
                    <YearDropdown
                      onYearChange={onYearChange}
                      year={effectiveCurrentYear}
                      questionId={questionId}
                      subQuestionId={subQuestionId}
                      locationAbbr={locationAbbr}
                      dropdownID="current-year-dropdown"
                      setDataLoading={setDataLoading}
                      subQuestionText={subQuestion ? subQuestion.SubQuestion : ""}
                      summaryText={(!question?.SubQuestions || question.SubQuestions.length === 0) ? (question ? question.SummaryText : "") : ""}
                    />
                  )}
                  {/** View 2: Show a Go button when the compare link is visible (i.e., not expanded yet) */}
                  {!isPatternsOverTime && !hasSubQuestionSelected && !showComparison && !APPLY_YEAR_ON_SELECT_VIEW2 && (
                    <button
                      type="button"
                      className="btn text-white btn ml-4"
                      onClick={() => onYearChange({ Year: effectiveCurrentYear })}
                      aria-label="Apply Year"
                      title="Apply Year"
                      style={{ backgroundColor: topicDictionary[question.TopicId].color }}
                    >
                      Go
                    </button>
                  )}
                </div>
              </div>
              { /* End of current location/year controls */}
              {/* Toggle link appears only for View 2 (non-POT, no subquestion) */}
              <div className="mt-2">
                {!showComparison ? (
                  <button
                    type="button"
                    id="compare-second-location-button"
                    className="btn btn-link p-0"
                    style={{ fontSize: 14 }}
                    onClick={() => setShowComparison(true)}
                  >
                    + Compare second location/year
                  </button>
                ) : (
                  <button
                    type="button"
                    id="remove-comparison-button"
                    className="btn btn-link p-0"
                    style={{ fontSize: 14, color: '#075290' }}
                    onClick={() => setShowComparison(false)}
                  >
                    - Remove comparison location/year
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        {/* Right-hand comparison selector column */}
        {(() => {
          const cfg = deriveComparisonUI();
          // Do not render comparison selector or sample size toggle on map or graph pages
          if (vizType === "map" || vizType === "graph") return null;

          const noSubQsShowingSummary = hasAllLocationsSummaryDetail;

          // Show Sample Size toggle in header:
          // - Never show for View #1 (qh-view-1) since it's rendered in Table.js instead
          // - For other views, show only when a subtopic (subquestion)
          //   or summary text view is active
          const showSampleToggle =
            currentViewClass !== "qh-view-1" &&
            (vizType === "table" || hasDetailSelection);

          // If nothing to show at all, bail out
          if (!cfg.showSelector && !showSampleToggle) return null;

          return (
            <>
              {cfg.showSelector && (
                <div
                  id="comparison-selector-container"
                  className={`${cfg.showYear ? "comparison-selector-row " : ""}  ${noSubQsShowingSummary ? "no-subquestions-summary-text" : ""}`}
                >
                  <div>
                    {cfg.mode !== "allLocations" && cfg.showYear && (
                      <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4 }}>{cfg.title}</div>
                    )}
                    <ComparisonSelector
                      locations={locations}
                      questionId={questionId}
                      subQuestionId={subQuestionId}
                      year={cfg.showYear ? effectiveCurrentYear : selectedData.year}
                      selectedData={selectedData}
                      excludeLocationAbbr={locationAbbr}
                      mode={cfg.mode}
                      showYear={cfg.showYear}
                      variant="question-header"
                      subQuestionText={subQuestion ? subQuestion.SubQuestion : ""}
                      summaryText={(!question?.SubQuestions || question.SubQuestions.length === 0) ? (question ? question.SummaryText : "") : ""}
                      onCompare={(payload) => {
                        onCompareSecondLocation && onCompareSecondLocation(payload);
                      }}
                      onCancel={() => setShowComparison(false)}
                      showCancel={false}
                    />
                  </div>
                </div>
              )}

              {showSampleToggle && (
                <SampleSizeToggle
                  id="tableShowSampleSizeCheckbox"
                  checked={!!showSampleSize}
                  onChange={(next) => {
                    if (typeof setShowSampleSize === "function") {
                      setShowSampleSize(next);
                    }
                  }}
                />
              )}
            </>
          );
        })()}
        </div>
      )}
      {/* Subquestions (ALL) */}
      <div className="row mb-2 question-header__subquestions">
        <div className="col-12">
          {isAllLocations && (
            <SubQuestions
              subQuestions={question.SubQuestions}
              handleSubQuestionChange={handleSubQuestionChange}
              subQuestionId={
                subQuestion ? subQuestion.SubQuestionId : subQuestionValue
              }
              question={question}
              vizType={vizType}
              isLoading={subQuestionLoading}
            />
          )}
        </div>
      </div>

  {/* Removed old post-location controls row; content now in QuestionVisualizationControls */}
    </div>
  );
};

export default QuestionHeader;